import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, startTransition } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages - Lazy loaded with prefetch hints
const LoginPage = lazy(() => import('./pages/LoginPage'));
const GoogleCallbackPage = lazy(() => import('./pages/GoogleCallbackPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('./pages/EmployeeDetailPage'));
const TimeLogsPage = lazy(() => import('./pages/TimeLogsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const AppClassificationsPage = lazy(() => import('./pages/AppClassificationsPage'));
const AppCatalogPage = lazy(() => import('./pages/AppCatalogPage'));
const LobsPage = lazy(() => import('./pages/LobsPage'));
const LobDetailPage = lazy(() => import('./pages/LobDetailPage'));
const FunctionDetailPage = lazy(() => import('./pages/FunctionDetailPage'));
const HolidaysPage = lazy(() => import('./pages/HolidaysPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

// Layout
import PageWrapper from './components/layout/PageWrapper';
import LoadingSpinner from './components/common/LoadingSpinner';

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-full py-8">
    <LoadingSpinner size="sm" />
  </div>
);

// Admin-only route guard: employees are self-scoped and must not reach the
// admin surfaces (they're API-blocked too, this just keeps the UI honest).
function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user?.role === 'employee') return <Navigate to="/dashboard" replace />;
  return children;
}

// Org App Classifications are desktop defaults — superadmin only (heads use the Function page).
function RequireSuperadmin({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'superadmin') return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          } />
          
          <Route path="/auth/google/callback" element={
            <Suspense fallback={<PageLoader />}>
              <GoogleCallbackPage />
            </Suspense>
          } />
          
          <Route path="/" element={<PageWrapper />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            {/* Available to everyone — the employee's single self-scoped screen */}
            <Route path="dashboard" element={<DashboardPage />} />
            {/* Admin surfaces — blocked for employees */}
            <Route path="time-logs" element={<RequireAdmin><TimeLogsPage /></RequireAdmin>} />
            <Route path="employees" element={<RequireAdmin><EmployeesPage /></RequireAdmin>} />
            <Route path="employees/:userId" element={<RequireAdmin><EmployeeDetailPage /></RequireAdmin>} />
            <Route path="reports" element={<RequireAdmin><ReportsPage /></RequireAdmin>} />
            <Route path="lobs" element={<RequireAdmin><LobsPage /></RequireAdmin>} />
            <Route path="lobs/:lobId" element={<RequireAdmin><LobDetailPage /></RequireAdmin>} />
            <Route path="functions/:functionId" element={<RequireAdmin><FunctionDetailPage /></RequireAdmin>} />
            <Route path="holidays" element={<RequireAdmin><HolidaysPage /></RequireAdmin>} />
            <Route path="app-classifications" element={<RequireSuperadmin><AppClassificationsPage /></RequireSuperadmin>} />
            {/* Shared catalog + the company-rule switch (see
                plan/GLOBAL_APP_CLASSIFICATION_APPROVAL_PLAN.md). Distinct from
                app-classifications above, which seeds desktop stamps. */}
            <Route path="app-catalog" element={<RequireSuperadmin><AppCatalogPage /></RequireSuperadmin>} />
            <Route path="settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
